export type DataMode = "emulator" | "production" | "sample";

export interface ResolvedConfig {
  mode: DataMode;
  firebase: { apiKey: string; authDomain: string; projectId: string; appId: string } | null;
  emulatorHost: string;
  /** Problems that must be shown to the user instead of silently degrading. */
  errors: string[];
}

type Env = Record<string, string | boolean | undefined>;

/**
 * Decide where data comes from. Rules:
 * - VITE_DATA_MODE=sample  -> bundled samples, never touches Firebase.
 * - VITE_DATA_MODE=production -> requires full config; misconfig is an ERROR,
 *   never a silent fallback to samples.
 * - otherwise (unset/emulator) -> Local Emulator Suite with a demo- project.
 */
export function resolveDataMode(env: Env): ResolvedConfig {
  const str = (k: string) => (typeof env[k] === "string" ? (env[k] as string).trim() : "");
  const requested = str("VITE_DATA_MODE") || "emulator";
  const emulatorHost = str("VITE_EMULATOR_HOST") || "127.0.0.1";

  if (requested === "sample") return { mode: "sample", firebase: null, emulatorHost, errors: [] };

  if (requested === "production") {
    const firebase = {
      apiKey: str("VITE_FIREBASE_API_KEY"),
      authDomain: str("VITE_FIREBASE_AUTH_DOMAIN"),
      projectId: str("VITE_FIREBASE_PROJECT_ID"),
      appId: str("VITE_FIREBASE_APP_ID"),
    };
    const missing = Object.entries(firebase).filter(([, v]) => !v).map(([k]) => k);
    const errors = missing.length ? [`Production mode is missing Firebase config: ${missing.join(", ")}.`] : [];
    if (firebase.projectId.startsWith("demo-")) errors.push("Production mode cannot use a demo- project ID.");
    return { mode: "production", firebase, emulatorHost, errors };
  }

  if (requested !== "emulator") {
    return { mode: "production", firebase: null, emulatorHost, errors: [`Unknown VITE_DATA_MODE "${requested}".`] };
  }

  const projectId = str("VITE_EMULATOR_PROJECT_ID") || "demo-noslides-pulse";
  return {
    mode: "emulator",
    firebase: { apiKey: "demo-api-key", authDomain: `${projectId}.firebaseapp.com`, projectId, appId: "demo-app" },
    emulatorHost,
    errors: projectId.startsWith("demo-") ? [] : ["Emulator project ID must start with demo- to avoid touching real resources."],
  };
}
